import { Link } from 'react-router-dom'
import { useKitBuilds, usePickLists, useProcedureTypeSummary, useProcessSteps } from '@/hooks/useDashboardData'
import { hoursLateText, qtyAgainst } from '@/lib/dashboardWidgetFormat'
import { windowLabel } from '@/lib/reportRange'
import { cn } from '@/lib/utils'
import type { WidgetBodyProps } from '../builtWidgets'
import { Sparkline, STATUS_COLORS } from '../charts'
import { PartsView, SeriesView, dailyRows, partsKind, seriesKind, type Part } from '../views'
import { WidgetQueryState, WidgetStat } from '../WidgetCard'

// ─── Processing, MES, Kits, Cleaning & Pick lists widgets (z8nrz7eqyn) ───────
//
// Each draws itself in the view the user chose (see `widgetViews`).


export function StepsInProgressWidget({ time, view }: WidgetBodyProps) {
  const q = useProcessSteps(time.query)
  const parts: Part[] = q.data
    ? [
        { label: 'Assigned', value: q.data.assigned, color: STATUS_COLORS.accentLight },
        { label: 'Started', value: q.data.started, color: STATUS_COLORS.accent },
        { label: 'Awaiting review', value: q.data.pending, color: '#f59e0b' },
      ]
    : []
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {q.data &&
        (view === 'number' ? (
          <WidgetStat
            value={q.data.started}
            href='/submissions'
            caption='Process steps started and not yet ended'
            trend={<Sparkline points={q.data.completedByDay} label='steps completed' color={STATUS_COLORS.good} />}
            footer={`${q.data.assigned} assigned · ${q.data.pending} waiting for completion review · ${q.data.completedInPeriod} completed`}
          />
        ) : (
          <PartsView view={partsKind(view)} parts={parts} totalLabel='live steps' />
        ))}
    </WidgetQueryState>
  )
}

export function LateStepsWidget({ time, view }: WidgetBodyProps) {
  const q = useProcessSteps(time.query)
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={view === 'list' && !!q.data && q.data.late === 0}
      emptyText='No assigned step is past its planned start.'
      onRetry={() => void q.refetch()}
    >
      {q.data &&
        (view === 'number' ? (
          <WidgetStat
            value={q.data.late}
            tone={q.data.late > 0 ? 'red' : 'blue'}
            href='/submissions'
            caption='Assigned steps past their planned start'
            footer={`${q.data.completedInPeriod} completed · ${windowLabel(time.window)}`}
          />
        ) : (
          <div className='flex flex-1 flex-col gap-2'>
            <p className='text-xs text-gray-500'>
              <span className='font-semibold text-red-600'>{q.data.late} late to start</span>
              {' · '}
              {q.data.completedInPeriod} completed · {windowLabel(time.window)}
            </p>
            <ul className='divide-y divide-gray-100'>
              {q.data.lateRows.map(row => (
                <li key={row.id}>
                  <Link
                    to={`/forms/${encodeURIComponent(row.submissionId)}`}
                    className='-mx-1 flex items-center gap-2 rounded px-1 py-1.5 hover:bg-gray-50'
                  >
                    <span className='min-w-0 flex-1'>
                      <span className='block truncate text-xs font-medium text-gray-800'>{row.formName}</span>
                      {row.patientMrn && <span className='block truncate text-[11px] text-gray-400'>{row.patientMrn}</span>}
                    </span>
                    <span className='shrink-0 text-[11px] font-medium text-red-600'>{hoursLateText(row.hoursLate)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </WidgetQueryState>
  )
}

export function MesRunsWidget({ time, view }: WidgetBodyProps) {
  const q = useProcedureTypeSummary('MES', time.query)
  const parts: Part[] = q.data
    ? [
        { label: 'Started', value: q.data.startedInPeriod, color: STATUS_COLORS.accent },
        { label: 'Completed', value: q.data.completedInPeriod, color: STATUS_COLORS.good },
        { label: 'Cancelled', value: q.data.cancelledInPeriod, color: STATUS_COLORS.muted },
      ]
    : []
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {q.data && (
        <div className='flex flex-1 flex-col justify-between gap-3'>
          {view === 'stack' && <WidgetStat value={q.data.open} href='/mes' caption='MES runs open now' />}
          <div>
            <p className='mb-1.5 text-[11px] text-gray-400'>
              {view !== 'stack' && `${q.data.open} open now · `}
              {windowLabel(time.window)}
            </p>
            <PartsView view={partsKind(view, 'stack')} parts={parts} totalLabel='runs' />
          </div>
          {q.data.overdue > 0 && <p className='text-[11px] font-medium text-red-600'>{q.data.overdue} open past due</p>}
        </div>
      )}
    </WidgetQueryState>
  )
}

export function KitBuildsWidget({ time, view }: WidgetBodyProps) {
  const q = useKitBuilds(time.query)
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {q.data &&
        (view === 'trend' || view === 'columns' ? (
          <div className='flex flex-1 flex-col gap-1'>
            <p className='text-xs text-gray-500'>
              <span className='font-semibold text-gray-900'>{q.data.unitsCompletedInPeriod}</span> kits completed ·{' '}
              {windowLabel(time.window)}
            </p>
            <SeriesView
              view={seriesKind(view, 'trend')}
              rows={dailyRows(q.data.completedByDay)}
              series={[{ key: 'count', label: 'Builds completed', color: STATUS_COLORS.good }]}
            />
          </div>
        ) : (
          <div className='flex flex-1 flex-col justify-between gap-3'>
            {view === 'stack' && (
              <WidgetStat
                value={q.data.unitsCompletedInPeriod}
                href='/kits'
                caption={`Kits completed · ${windowLabel(time.window)}`}
                trend={<Sparkline points={q.data.completedByDay} label='builds completed' color={STATUS_COLORS.good} />}
              />
            )}
            <PartsView
              view={partsKind(view, 'stack')}
              totalLabel='builds'
              parts={[
                { label: 'In progress', value: q.data.inProgress, color: STATUS_COLORS.accent },
                { label: 'Completed', value: q.data.completedInPeriod, color: STATUS_COLORS.good },
                { label: 'Cancelled', value: q.data.cancelledInPeriod, color: STATUS_COLORS.muted },
              ]}
            />
          </div>
        ))}
    </WidgetQueryState>
  )
}

export function CleaningDueWidget({ time, view }: WidgetBodyProps) {
  const q = useProcedureTypeSummary('Cleaning', time.query)
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {q.data && (
        <div className='flex flex-1 flex-col justify-between gap-3'>
          {(view === 'stack' || view === 'number') && (
            <WidgetStat
              value={q.data.overdue + q.data.dueSoon}
              href='/cleaning'
              tone={q.data.overdue > 0 ? 'red' : q.data.dueSoon > 0 ? 'amber' : 'blue'}
              caption='Cleaning runs overdue or due within 7 days'
            />
          )}
          {view !== 'number' && <PartsView
            view={partsKind(view, 'stack')}
            totalLabel='open'
            parts={[
              { label: 'Overdue', value: q.data.overdue, color: STATUS_COLORS.bad },
              { label: 'Due soon', value: q.data.dueSoon, color: '#f59e0b' },
              { label: 'Other open', value: Math.max(0, q.data.open - q.data.overdue - q.data.dueSoon), color: STATUS_COLORS.accentLight },
            ]}
          />}
        </div>
      )}
    </WidgetQueryState>
  )
}

export function OpenPickListsWidget({ view }: WidgetBodyProps) {
  const q = usePickLists()
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {q.data && (
        <div className='flex flex-1 flex-col justify-between gap-3'>
          {view === 'stack' && (
            <WidgetStat
              value={q.data.open.total}
              href='/inventory/pick-lists'
              caption='Pick lists not yet completed'
              footer={q.data.overdue > 0 ? <span className='font-medium text-red-600'>{q.data.overdue} past required-by</span> : undefined}
            />
          )}
          <PartsView
            view={partsKind(view, 'stack')}
            totalLabel='open'
            parts={[
              { label: 'Draft', value: q.data.open.draft, color: STATUS_COLORS.accentLighter },
              { label: 'Open', value: q.data.open.open, color: STATUS_COLORS.accentLight },
              { label: 'In progress', value: q.data.open.inProgress, color: STATUS_COLORS.accent },
            ]}
          />
        </div>
      )}
    </WidgetQueryState>
  )
}

export function PickListShortagesWidget({ view }: WidgetBodyProps) {
  const q = usePickLists()
  // Bars: which materials run short most often across open lists.
  const byMaterial = new Map<string, number>()
  for (const r of q.data?.shortageRows ?? []) byMaterial.set(r.materialName, (byMaterial.get(r.materialName) ?? 0) + 1)
  const materialParts: Part[] = [...byMaterial]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([label, value]) => ({ label, value, color: STATUS_COLORS.bad }))
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={view !== 'number' && !!q.data && q.data.shortages === 0}
      emptyText='Every open pick-list line can be filled.'
      onRetry={() => void q.refetch()}
    >
      {q.data &&
        (view === 'number' ? (
          <WidgetStat
            value={q.data.shortages}
            tone={q.data.shortages > 0 ? 'red' : 'blue'}
            href='/inventory/pick-lists'
            caption='Open pick-list lines that cannot be filled'
            footer={`across ${q.data.open.total} open pick lists`}
          />
        ) : view === 'bars' || view === 'table' || view === 'donut' ? (
          <div className='flex flex-1 flex-col gap-1'>
            <p className='text-[11px] text-gray-400'>Short lines by material</p>
            <PartsView view={partsKind(view)} parts={materialParts} totalLabel='short lines' />
          </div>
        ) : (
          <div className='flex flex-1 flex-col gap-2'>
            <p className='text-xs text-gray-500'>
              <span className='font-semibold text-red-600'>
                {q.data.shortages} line{q.data.shortages === 1 ? '' : 's'} short
              </span>{' '}
              on open pick lists
            </p>
            <ul className='divide-y divide-gray-100'>
              {q.data.shortageRows.map((row, i) => (
                <li key={`${row.pickListId}-${i}`}>
                  <Link
                    to={`/inventory/pick-lists/${encodeURIComponent(row.pickListId)}`}
                    className='-mx-1 flex items-center gap-2 rounded px-1 py-1.5 hover:bg-gray-50'
                  >
                    <span className='min-w-0 flex-1'>
                      <span className='block truncate text-xs font-medium text-gray-800'>{row.materialName}</span>
                      <span className='block truncate text-[11px] text-gray-400'>{row.pickListCode}</span>
                    </span>
                    <span
                      className={cn('shrink-0 text-[11px] font-medium', row.reason === 'SHORT' ? 'text-red-600' : 'text-amber-600')}
                      title={row.reason === 'SHORT' ? 'Picked short' : 'Stock on hand did not cover it when the list was made'}
                    >
                      {qtyAgainst(row.onHandQty, row.qtyRequired, row.uom)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </WidgetQueryState>
  )
}
